import React, { createContext, useContext, useState } from 'react'

import type {
  HierarchicalRole,
  UserTierAssignment,
} from '../types/hierarchicalRoles'

type UserTierAssignmentsContextType = {
  userTierAssignments: UserTierAssignment[]
  setUserTierAssignments: React.Dispatch<
    React.SetStateAction<UserTierAssignment[]>
  >
  addRoleAssignment: (
    assignment: Omit<UserTierAssignment, 'id' | 'effectivePermissions'>,
  ) => void
  updateRoleAssignment: (
    assignmentId: string,
    updates: Partial<UserTierAssignment>,
  ) => void
  removeRoleAssignment: (assignmentId: string) => void
  getUserAssignments: (userId: string) => UserTierAssignment[]
  availableRoles: HierarchicalRole[]
}

const UserTierAssignmentsContext = createContext<
  UserTierAssignmentsContextType | undefined
>(undefined)

export const useUserTierAssignments = (): UserTierAssignmentsContextType => {
  const context = useContext(UserTierAssignmentsContext)
  if (context === undefined) {
    throw new Error(
      'useUserTierAssignments must be used within a UserTierAssignmentsProvider',
    )
  }
  return context
}

type UserTierAssignmentsProviderProps = {
  children: React.ReactNode
}

export const UserTierAssignmentsProvider = ({
  children,
}: UserTierAssignmentsProviderProps): React.JSX.Element => {
  const [userTierAssignments, setUserTierAssignments] = useState<
    UserTierAssignment[]
  >([])

  const addRoleAssignment = (
    assignment: Omit<UserTierAssignment, 'id' | 'effectivePermissions'>,
  ): void => {
    const newAssignment: UserTierAssignment = {
      ...assignment,
      id: `assignment-${Date.now()}`,
      effectivePermissions: [],
    }

    setUserTierAssignments(prev => [...prev, newAssignment])
  }

  const updateRoleAssignment = (
    assignmentId: string,
    updates: Partial<UserTierAssignment>,
  ): void => {
    setUserTierAssignments(prev =>
      prev.map(assignment =>
        assignment.id === assignmentId
          ? { ...assignment, ...updates }
          : assignment,
      ),
    )
  }

  const removeRoleAssignment = (assignmentId: string): void => {
    setUserTierAssignments(prev => prev.filter(a => a.id !== assignmentId))
    console.log('Removed role assignment:', assignmentId)
  }

  const getUserAssignments = (userId: string): UserTierAssignment[] => {
    return userTierAssignments.filter(a => a.userId === userId && a.isActive)
  }

  const value: UserTierAssignmentsContextType = {
    userTierAssignments,
    setUserTierAssignments,
    addRoleAssignment,
    updateRoleAssignment,
    removeRoleAssignment,
    getUserAssignments,
    availableRoles: [],
  }

  return (
    <UserTierAssignmentsContext.Provider value={value}>
      {children}
    </UserTierAssignmentsContext.Provider>
  )
}
